import { message } from 'antd';
import { useCallback } from 'react';
import { fetchAdminProductsPage } from '../../services';
import type { AdminProduct, FetchAdminProductsOptions, UseAdminProductCatalogArgs } from '../interfaces';
import {
  ADMIN_PRODUCTS_PAGE_SIZE,
  createDraftPriceMap,
  createDraftStockMap,
} from './adminProductForm.utils';
import { getApiErrorMessage } from '../../utils/apiError';

type UseAdminProductCatalogFetchArgs = Pick<
  UseAdminProductCatalogArgs,
  | 'setBaseProducts'
  | 'setCurrentProductsPage'
  | 'setDraftPriceById'
  | 'setDraftStockById'
  | 'setLoadingMoreProducts'
  | 'setLoadingProducts'
  | 'setProducts'
  | 'setProductsHasMore'
  | 'setProductsSkip'
  | 'setProductsTotal'
> & {
  productsRequestIdRef: React.MutableRefObject<number>;
};

export const useAdminProductCatalogFetch = ({
  productsRequestIdRef,
  setBaseProducts,
  setCurrentProductsPage,
  setDraftPriceById,
  setDraftStockById,
  setLoadingMoreProducts,
  setLoadingProducts,
  setProducts,
  setProductsHasMore,
  setProductsSkip,
  setProductsTotal,
}: UseAdminProductCatalogFetchArgs) => {
  const fetchProducts = useCallback(async (options?: FetchAdminProductsOptions) => {
    const append = options?.append ?? false;
    const page = options?.page ?? 1;
    const skip = options?.skip ?? (page - 1) * ADMIN_PRODUCTS_PAGE_SIZE;
    const search = options?.search ?? '';
    const requestId = productsRequestIdRef.current + 1;

    productsRequestIdRef.current = requestId;
    append ? setLoadingMoreProducts(true) : setLoadingProducts(true);

    try {
      const response = await fetchAdminProductsPage({
        take: ADMIN_PRODUCTS_PAGE_SIZE,
        skip,
        search: search || undefined,
      });
      const nextProducts = response.products || [];
      let mergedProducts: AdminProduct[] = nextProducts;

      if (productsRequestIdRef.current !== requestId) {
        return;
      }

      setBaseProducts((prev) => {
        mergedProducts = append ? [...prev, ...nextProducts] : nextProducts;
        return mergedProducts;
      });
      setProducts(mergedProducts);
      setCurrentProductsPage(page);
      setProductsSkip(skip + nextProducts.length);
      setProductsTotal(response.productsCount || 0);
      setProductsHasMore(skip + nextProducts.length < (response.productsCount || 0));
      setDraftStockById((prev) => ({ ...(!append ? {} : prev), ...createDraftStockMap(nextProducts) }));
      setDraftPriceById((prev) => ({ ...(!append ? {} : prev), ...createDraftPriceMap(nextProducts) }));
    } catch (error) {
      if (productsRequestIdRef.current === requestId) {
        message.error(getApiErrorMessage(error, 'Failed to load products.'));
      }
    } finally {
      append ? setLoadingMoreProducts(false) : setLoadingProducts(false);
    }
  }, [
    productsRequestIdRef,
    setBaseProducts,
    setCurrentProductsPage,
    setDraftPriceById,
    setDraftStockById,
    setLoadingMoreProducts,
    setLoadingProducts,
    setProducts,
    setProductsHasMore,
    setProductsSkip,
    setProductsTotal,
  ]);

  return { fetchProducts };
};
